import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Tooltip from '@mui/material/Tooltip';
import dayjs from 'dayjs';

import { BreadcrumbHeader, FilterTabs, DataTable, StatusBadge, EmptyState, ActionMenu } from '../../components/shared';
import { vendorShipments, partReplenishmentOrders, warehouses } from '../../services/dataClient';

// Break at the last space within `max` chars, or hard-cut if no space
import ContentCard from "../../components/shared/ContentCard";function truncate(text, max = 14) {
  if (!text || text.length <= max) return { short: text, truncated: false };
  const breakAt = text.lastIndexOf(' ', max);
  const short = (breakAt > 4 ? text.slice(0, breakAt) : text.slice(0, max)) + '…';
  return { short, truncated: true };
}

// ─── Location type badge ───────────────────────────────────────────────────────
const LOC_TYPE_STYLE = {
  FSL: { bg: '#E0F2F1', color: '#00695C' },
  Warehouse: { bg: '#ECEFF1', color: '#455A64' },
  'On-Site': { bg: '#FFF3E0', color: '#E65100' }
};

function LocationTypeBadge({ type }) {
  if (!type) return null;
  const style = LOC_TYPE_STYLE[type] ?? { bg: 'var(--grey-100)', color: 'var(--grey-600)' };
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex',
        alignItems: 'center',
        px: '7px',
        py: '2px',
        borderRadius: '4px',
        fontSize: '0.7rem',
        fontWeight: 600,
        backgroundColor: style.bg,
        color: style.color,
        whiteSpace: 'nowrap',
        flexShrink: 0
      }}>
      
      {type}
    </Box>);

}

// ─── Constants ────────────────────────────────────────────────────────────────
const TABS = [
{ label: 'All', value: 'All' },
{ label: 'Active', value: 'Active' },
{ label: 'Pending', value: 'Pending' },
{ label: 'In Transit', value: 'In Transit' },
{ label: 'Delivered', value: 'Delivered' }];

function getNextStatuses(current) {
  if (current === 'Active' || current === 'Pending') return ['In Transit'];
  if (current === 'In Transit') return ['Delivered'];
  return [];
}

// ─── Summary bar ──────────────────────────────────────────────────────────────
function SummaryBar({ data }) {
  const total = data.length;
  const pending = data.filter((s) => s.status === 'Pending').length;
  const inTransit = data.filter((s) => s.status === 'In Transit').length;
  const delivered = data.filter((s) => s.status === 'Delivered').length;

  const items = [
  { label: 'Total', value: total, color: 'var(--grey-700)' },
  { label: 'Pending', value: pending, color: '#E65100' },
  { label: 'In Transit', value: inTransit, color: '#4527A0' },
  { label: 'Delivered', value: delivered, color: '#2E7D32' }];

  return (
    <ContentCard
      sx={{ display: 'flex',
        gap: 0,

        overflow: 'hidden'
      }}>
      
      {items.map((item, i) =>
      <Box
        key={item.label}
        sx={{ flex: 1,
          px: 3,
          py: '12px',
          borderRight: i < items.length - 1 ? '1px solid var(--grey-200)' : 'none'
        }}>
        
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: item.color, lineHeight: 1 }}>
            {item.value}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mt: '4px' }}>
            {item.label}
          </Typography>
        </Box>
      )}
    </ContentCard>);

}

// ─── Update Status modal ───────────────────────────────────────────────────────
function UpdateStatusModal({ open, shipment, onConfirm, onCancel }) {
  const nextStatuses = shipment ? getNextStatuses(shipment.status) : [];
  const [newStatus, setNewStatus] = useState('');

  const handleEnter = () => setNewStatus(nextStatuses[0] ?? '');

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {if (reason !== 'backdropClick') onCancel();}}
      TransitionProps={{ onEnter: handleEnter }}
      maxWidth="xs"
      fullWidth>
      
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Update Status
        <IconButton size="small" onClick={onCancel} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        <FormControl size="small" fullWidth>
          <InputLabel>New Status</InputLabel>
          <Select
            label="New Status"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}>
            
            {nextStatuses.map((s) =>
            <MenuItem key={s} value={s}>{s}</MenuItem>
            )}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography
          onClick={onCancel}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
          
          Cancel
        </Typography>
        <Button
          variant="contained"
          size="small"
          disabled={!newStatus}
          onClick={() => onConfirm(newStatus)}
          sx={{ backgroundColor: '#009688',
            '&:hover': { backgroundColor: '#00796B' },
            '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: 'none',
            px: 2
          }}>
          
          Update
        </Button>
      </DialogActions>
    </Dialog>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function ReplenishmentShipmentsPage() {
  const navigate = useNavigate();

  const [allShipments, setAllShipments] = useState(vendorShipments);
  const [activeTab, setActiveTab] = useState('All');
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: 'estimatedDeliveryDate', direction: 'asc' });
  const [showBanner, setShowBanner] = useState(false);
  const [bannerMessage, setBannerMessage] = useState('');
  const [updateModal, setUpdateModal] = useState(null);

  const today = dayjs().format('YYYY-MM-DD');

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleUpdateStatus = (newStatus) => {
    setAllShipments((prev) =>
    prev.map((s) => s.id === updateModal.id ? { ...s, status: newStatus } : s)
    );
    setBannerMessage(`Shipment ${updateModal.shipmentNumber} updated to ${newStatus}.`);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 4000);
    setUpdateModal(null);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allShipments
    .filter((s) => activeTab === 'All' || s.status === activeTab)
    .filter((s) =>
    !q ||
    s.shipmentNumber.toLowerCase().includes(q) ||
    s.vendorName.toLowerCase().includes(q) ||
    s.trackingNumber.toLowerCase().includes(q) ||
    (s.stockLocation ?? '').toLowerCase().includes(q)
    )
    .sort((a, b) => {
      const va = a[sortConfig.field] ?? '';
      const vb = b[sortConfig.field] ?? '';
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
  }, [allShipments, activeTab, search, sortConfig]);

  const columns = [
  {
    field: 'shipmentNumber',
    headerName: 'Shipment #',
    sortable: true,
    renderCell: (row) =>
    <Typography
      onClick={(e) => {e.stopPropagation();navigate(`/shipments/replenishment-shipments/${row.id}`);}}
      sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
      
          {row.shipmentNumber}
        </Typography>

  },
  {
    field: 'vendorName',
    headerName: 'Supplier',
    sortable: true,
    renderCell: (row) => {
      const { short, truncated } = truncate(row.vendorName);
      return (
        <Tooltip title={truncated ? row.vendorName : ''} placement="top" arrow enterDelay={300} disableHoverListener={!truncated}>
            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>{short ?? '—'}</Typography>
          </Tooltip>);

    }
  },
  {
    field: 'partNumber',
    headerName: 'Part #',
    sortable: true,
    renderCell: (row) => {
      const linkedOrder = partReplenishmentOrders.find((o) => o.id === row.partReplenishmentOrderId);
      const extraCount = linkedOrder?.lineItems ? linkedOrder.lineItems.length - 1 : 0;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={{ fontSize: '0.875rem' }}>{row.partNumber ?? '—'}</Typography>
            {extraCount > 0 &&
          <Box sx={{ px: '6px', py: '1px', borderRadius: '4px', backgroundColor: 'var(--grey-100)', border: '1px solid var(--grey-200)' }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--grey-500)', whiteSpace: 'nowrap' }}>
                  +{extraCount} more
                </Typography>
              </Box>
          }
          </Box>);

    }
  },
  {
    field: 'stockLocation',
    headerName: 'Stock Location',
    sortable: true,
    renderCell: (row) => {
      const wh = warehouses.find((w) => w.name === row.stockLocation);
      const { short: locShort, truncated: locTrunc } = truncate(row.stockLocation, 16);
      return (
        <Tooltip title={locTrunc ? row.stockLocation : ''} placement="top" arrow enterDelay={300} disableHoverListener={!locTrunc}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography sx={{ fontSize: '0.875rem' }}>{locShort ?? '—'}</Typography>
              {wh && <LocationTypeBadge type={wh.type} />}
            </Box>
          </Tooltip>);

    }
  },
  { field: 'carrier', headerName: 'Carrier', sortable: true },
  { field: 'trackingNumber', headerName: 'Tracking #', sortable: false },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: (row) => <StatusBadge status={row.status} />
  },
  {
    field: 'estimatedDeliveryDate',
    headerName: 'Est. Delivery',
    sortable: true,
    renderCell: (row) => {
      const overdue = row.estimatedDeliveryDate < today && row.status !== 'Delivered';
      return (
        <Typography sx={{ fontSize: '0.875rem', color: overdue ? '#E65100' : 'inherit', fontWeight: overdue ? 600 : 400 }}>
            {row.estimatedDeliveryDate}
          </Typography>);

    }
  },
  {
    field: 'partReplenishmentOrderNumber',
    headerName: 'Linked Order #',
    sortable: false,
    renderCell: (row) =>
    row.partReplenishmentOrderNumber ?
    <Typography
      onClick={(e) => {e.stopPropagation();navigate(`/orders/part-replenishment/${row.partReplenishmentOrderId}`);}}
      sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
      
            {row.partReplenishmentOrderNumber}
          </Typography> :

    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>

  },
  {
    field: '_actions',
    headerName: '',
    sortable: false,
    renderCell: (row) => {
      const nextStatuses = getNextStatuses(row.status);
      const actions = [
      { label: 'View Detail', onClick: () => navigate(`/shipments/replenishment-shipments/${row.id}`) },
      ...(nextStatuses.length > 0 ?
      [{ label: 'Update Status', onClick: () => setUpdateModal(row) }] :
      []),
      ...(row.partReplenishmentOrderId ?
      [{ label: 'View Linked Order', onClick: () => navigate(`/orders/part-replenishment/${row.partReplenishmentOrderId}`) }] :
      [])];

      return (
        <Box onClick={(e) => e.stopPropagation()}>
            <ActionMenu actions={actions} hideUntilRowHover />
          </Box>);

    }
  }];

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Success banner */}
      {showBanner &&
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '10px',
          backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7',
          borderRadius: '8px', px: 2, py: '12px'
        }}>
        
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>
            {bannerMessage}
          </Typography>
        </Box>
      }

      <BreadcrumbHeader
        title="Replenishment Shipments"
        crumbs={[
        { label: 'Shipments' },
        { label: 'Replenishment Shipments' }]
        } />

      <SummaryBar data={allShipments} />

      <ContentCard sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FilterTabs tabs={TABS} value={activeTab} onChange={setActiveTab} />

        <TextField
          size="small"
          placeholder="Search by Shipment #, Supplier, Tracking #, or Stock Location…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment:
            <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'var(--grey-400)' }} />
              </InputAdornment>

          }}
          sx={{ minWidth: 400, maxWidth: 400, backgroundColor: 'var(--paper)', borderRadius: 1 }} />

        <Box sx={{ borderRadius: '8px', border: '1px solid var(--grey-200)', overflow: 'hidden' }}>
          {filtered.length === 0 ?
          <EmptyState title="No shipments found" description="Try adjusting your filter or search query." /> :

          <DataTable
            columns={columns}
            data={filtered}
            onRowClick={(row) => navigate(`/shipments/replenishment-shipments/${row.id}`)}
            sortConfig={sortConfig}
            onSort={handleSort} />

          }
        </Box>
      </ContentCard>

      {/* Update Status modal */}
      <UpdateStatusModal
        open={Boolean(updateModal)}
        shipment={updateModal}
        onConfirm={handleUpdateStatus}
        onCancel={() => setUpdateModal(null)} />

    </Box>);

}